import { ReportCard } from "./report-card";
import { RefreshButton } from "@/components/list/refresh-button";
import {
  createPaginationStore,
  DataTablePagination,
} from "@/components/zthd/data-table-pagination-mysql";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { ReportToolbar } from "./report-toolbar";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import type { Report } from "@/types/domain";

const usePaginationStore = createPaginationStore(0, 25, "report");

const ReportTable = ({ params }: { params?: Record<string, unknown> }) => {
  const [searchParams] = useSearchParams();
  const searchParamsObj = Object.fromEntries(searchParams.entries());
  const [name, setName] = useState("");
  const [result, setResult] = useState<string | undefined>(undefined);
  const paginationStore = usePaginationStore();
  const { pageIndex, pageSize } = paginationStore;
  const setPageIndex = usePaginationStore((state) => state.setPageIndex);

  const apiParams = useMemo(() => {
    const paramsObj: Record<string, unknown> = {
      ...searchParamsObj,
      ...params,
      pageIndex,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "desc",
    };
    if (name) paramsObj.name = name;
    if (result) paramsObj.result = result;
    return paramsObj;
  }, [searchParamsObj, params, pageIndex, pageSize, name, result]);

  const { data, isLoading, isValidating, mutate } = useSWR<{
    data: Report[];
    total: number;
  }>(swrKey("/reports", apiParams), { keepPreviousData: true });

  useEffect(() => {
    setPageIndex(0);
  }, [searchParams, setPageIndex]);

  const total = data?.total ?? 0;
  const reports = data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      {/* Toolbar left, RefreshButton atom pinned to the row's far right —
          same layout as PrdTable / app versions (#32 convention). */}
      <div className="flex w-full">
        <ReportToolbar
          name={name}
          onNameChange={(v) => {
            setName(v);
            setPageIndex(0);
          }}
          result={result}
          onResultChange={(v) => {
            setResult(v);
            setPageIndex(0);
          }}
        />
        <div className="ml-auto flex space-x-2">
          <RefreshButton
            spinning={isValidating}
            onClick={() => mutate()}
          />
        </div>
      </div>
      {isLoading && !data && <ListLoading>Loading reports…</ListLoading>}
      {!isLoading && reports.length === 0 && <ListEmpty>No reports.</ListEmpty>}
      <div className="flex flex-col gap-1">
        {reports.map((report: Report) => (
          <ReportCard key={report.id} report={report} />
        ))}
      </div>
      <DataTablePagination total={total} store={paginationStore} />
    </div>
  );
};

export default ReportTable;
